import React, { FC } from 'react';
import { Tooltip } from 'antd';
import {
  ArrowBigUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowRightToLine,
  ArrowUp,
  Command,
  CornerDownLeft,
  Option,
  Space
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import cx from 'classnames';
import styles from './index.module.css';
import type { NamedShortcutKey, ShortcutPiece } from './shortcutSections';

type Props = {
  pieces: ShortcutPiece[];
  isMac: boolean;
  container?: HTMLElement | null;
};

type KeyPresentation = {
  icon?: LucideIcon;
  text?: string;
  tooltip?: string;
  wide?: boolean;
};

const namedKeyPresentation = (
  id: NamedShortcutKey,
  isMac: boolean
): KeyPresentation => {
  switch (id) {
    case 'mod':
      return isMac ? { icon: Command, tooltip: 'Command' } : { text: 'Ctrl' };
    case 'ctrl':
      return { text: 'Ctrl' };
    case 'alt':
      return isMac ? { icon: Option, tooltip: 'Option' } : { text: 'Alt' };
    case 'shift':
      return { icon: ArrowBigUp, tooltip: 'Shift' };
    case 'enter':
      return { icon: CornerDownLeft, tooltip: 'Enter' };
    case 'esc':
      return { text: 'Esc' };
    case 'space':
      return { icon: Space, tooltip: 'Space', wide: true };
    case 'tab':
      return { icon: ArrowRightToLine, tooltip: 'Tab' };
    case 'up':
      return { icon: ArrowUp, tooltip: 'Up' };
    case 'down':
      return { icon: ArrowDown, tooltip: 'Down' };
    case 'left':
      return { icon: ArrowLeft, tooltip: 'Left' };
    case 'right':
      return { icon: ArrowRight, tooltip: 'Right' };
  }
};

const ShortcutKeycap: FC<{
  presentation: KeyPresentation;
  container?: HTMLElement | null;
}> = ({ presentation, container }) => {
  const { icon: Icon, text, tooltip, wide } = presentation;
  const keycap = (
    <span
      className={cx(styles.shortcutKey, {
        [styles.shortcutKeyWide]: wide,
        [styles.shortcutKeyHint]: !!tooltip
      })}
      aria-label={tooltip}
    >
      {Icon ? <Icon size={13} strokeWidth={2} aria-hidden /> : text}
    </span>
  );

  if (!tooltip) {
    return keycap;
  }

  return (
    <Tooltip
      title={tooltip}
      mouseEnterDelay={0.25}
      overlayStyle={{ zIndex: 2147483001 }}
      getPopupContainer={triggerNode =>
        container || triggerNode.parentElement || document.body
      }
    >
      {keycap}
    </Tooltip>
  );
};

export const ShortcutKeys: FC<Props> = ({ pieces, isMac, container }) => (
  <span className={styles.shortcutKeys}>
    {pieces.map((piece, index) => {
      const key = `${index}-${JSON.stringify(piece)}`;

      if (typeof piece === 'string') {
        return (
          <ShortcutKeycap
            key={key}
            presentation={namedKeyPresentation(piece, isMac)}
            container={container}
          />
        );
      }

      if ('sep' in piece) {
        return (
          <span key={key} className={styles.shortcutSep}>
            {piece.sep}
          </span>
        );
      }

      if ('example' in piece) {
        return (
          <code key={key} className={styles.shortcutExample}>
            {piece.example}
          </code>
        );
      }

      return (
        <ShortcutKeycap
          key={key}
          presentation={{ text: piece.char }}
          container={container}
        />
      );
    })}
  </span>
);

export default ShortcutKeys;
